import { createBrowserHistory } from 'history'

import { spawn } from '@app/packages/disposable/spawn'

import { HistoryEvent } from './HistoryEvent'
import type { History, HistoryInterceptAction, HistoryInterceptCallback, Location, LocationInit, NavigationAction } from './types'

export class BrowserHistory implements History {
  private history = createBrowserHistory()
  private interceptors: { value: HistoryInterceptCallback }[] = []
  private handleError?: (error: unknown) => void

  constructor({ handleError }: { handleError?: (error: unknown) => void } = {}) {
    this.handleError = handleError
  }

  get location() {
    return this.history.location as Location
  }

  get action() {
    return this.history.action
  }

  intercept(cb: HistoryInterceptCallback) {
    const val = { value: cb }
    this.interceptors.push(val)
    return () => {
      this.interceptors = this.interceptors.filter(i => i !== val)
    }
  }

  push(location: string | LocationInit, state?: any) {
    spawn(async () => {
      const prevented = await this.handleInterception('PUSH', location)
      if (prevented) return
      return this.history.push(location, state)
    })
  }

  replace(location: string | LocationInit, state?: any) {
    spawn(async () => {
      const prevented = await this.handleInterception('REPLACE', location)
      if (prevented) return
      return this.history.replace(location, state)
    })
  }

  listen(listener: (location: Location, action: NavigationAction) => void) {
    return this.history.listen(({ location, action }) => listener(location as Location, action))
  }

  back() {
    return this.history.back()
  }

  private handleInterception = (() => {
    let ab: AbortController | null = null
    return async (action: 'PUSH' | 'REPLACE', location: string | LocationInit): Promise<boolean> => {
      if (!this.interceptors.length) return false

      const lab = new AbortController()
      ab?.abort()
      ab = lab
      const event = new HistoryEvent<HistoryInterceptAction>({
        action: { type: action, location: location as Location },
        signal: lab.signal,
      })
      for (const interceptor of this.interceptors) {
        try {
          await interceptor.value(event)
        } catch (e) {
          try {
            this.handleError?.(e)
          } catch {
            console.warn('Error in handleError callback', e)
          }
        }
        if (event.isPrevented) return true
        if (event.signal.aborted) return true
      }
      return false
    }
  })()
}
